import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  compose, every, get, getOr, identity, includes, isEmpty, keys, map, mapValues, maxBy, pickBy, reduce, size, some, toNumber,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Checkbox, TableRow, TableCell } from '@material-ui/core';
import { Shimmer, Table } from '../../../common/components';
import { formatPhoneNumber } from '../../../common/utils';
import messages from '../../messages';
import DeleteConfirmDialog from '../DeleteConfirmDialog';
import DeleteReconfirmDialog from '../DeleteReconfirmDialog';
import TableToolBar from '../TableToolBar';

const cols = [
  { id: 'checkbox', style: css` width: 5%;` },
  { id: 'group', style: css`width: 14%;` },
  { id: 'clazzNumber', style: css`width: 5%;` },
  { id: 'studentName', style: css`width: 24%;` },
  { id: 'studentPhoneNumber', style: css`width: 14%;` },
  { id: 'guardianName', style: css`width: 24%;` },
  { id: 'guardianPhoneNumber', style: css`width: 14%;` },
];

const S = {
  Checkbox: styled(({ classes, dark, ...otherProps }) => (
    <Checkbox
      classes={{
        ...classes,
        checked: 'checked',
      }}
      {...otherProps}
    />
  ))`
    color: ${({ dark, theme }) => theme.palette.grey[dark ? 500 : 400]};
    :hover {
      color: ${({ theme }) => theme.palette.grey[500]};
    }
    &.Mui-checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  Pad: styled.div`
    padding-left: 20%;
  `,
  TableRow: styled(({ classes, ...otherProps }) => (
    <TableRow
      classes={{
        ...classes,
        hover: 'hover',
      }}
      {...otherProps}
    />
  ))`
    height: 60px;
    cursor: pointer;
    &.hover:hover {
      background-color: ${({ theme }) => theme.palette.blueGrey[25]};
    }
    ${({ variant }) => variant === 'head' && css`
      cursor: auto;
    `}
  `,
  TableCell: styled(({ active, ...otherProps }) => <TableCell {...otherProps} />)`
    padding: 0;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    :nth-child(1) {
      text-align: center;
      cursor: auto;
    }
    :nth-child(3) {
      font-size: ${({ theme }) => theme.typography.pxToRem(13)};
      text-align: center;
    }
    ${({ variant, theme }) => variant === 'head' && css`
      && {
        color: ${theme.palette.grey[600]};
        font-size: ${theme.typography.pxToRem(13)};
        font-weight: 500;
      }
    `}
  `,
  Wrapper: styled.div`
    width: 100%;
    position: relative;
  `,
  TableToolBar: styled(TableToolBar)`
    position: absolute;
    top: 0;
    left: 0;
  `,
};

const ContactTable = ({
  all, contacts, contactsDeleted, onOpen, onSelect, onDeselect, onDelete,
}) => {
  const [checked, setChecked] = useState({});
  const [prevContacts, setPrevContacts] = useState(null);
  const [open, setOpen] = useState(null);

  const relationships = useMemo(() => ({
    mother: '모',
    father: '부',
    grand_mother: '조모',
    grand_father: '조부',
    etc: '기타',
  }), []);

  const checkedSome = useMemo(() => compose(
    some(identity),
    pickBy((_, key) => compose(
      includes(toNumber(key)),
      map(contact => contact.id),
      get('rows'),
    )(contacts.data)),
  )(checked), [checked, contacts.data]);

  const checkedAll = useCallback(compose(
    every(identity),
    pickBy((_, key) => compose(
      includes(toNumber(key)),
      map(contact => contact.id),
      get('rows'),
    )(contacts.data)),
  ), [contacts.data]);

  const toggleAllChecked = useCallback(() => {
    const cids = map(contact => contact.id, get('rows', contacts.data));

    setChecked(prevChecked => (every(identity, prevChecked)
      ? mapValues(() => false, prevChecked)
      : {
        ...prevChecked,
        ...reduce((acc, cur) => ({
          ...acc,
          [cur]: !checkedAll(prevChecked),
        }), {}, cids),
      }));
  }, [checkedAll, contacts.data]);

  const checkAll = useCallback(() => setChecked(prevChecked => ({
    ...prevChecked,
    ...compose(
      mapValues(() => true),
      pickBy((_, key) => compose(
        includes(toNumber(key)),
        map(contact => contact.id),
        get('rows'),
      )(contacts.data)),
    )(prevChecked),
  })), [contacts.data]);
  const uncheckAll = useCallback(() => setChecked(mapValues(() => false)), []);
  const openDeleteConfirmDialog = useCallback(() => setOpen('deleteConfirm'), []);
  const openDeleteReconfirmDialog = useCallback(() => setOpen('deleteReconfirm'), []);
  const closeDialog = useCallback(() => setOpen(null), []);

  const getRow = useCallback((contact) => {
    const groupName = contact.class_group
      ? `${contact.class_group.grade}학년 ${contact.class_group.class_name}반`
      : (getOr('-', 'name', maxBy(customGroup => Date.parse(customGroup.created_at), contact.custom_groups)) || '미지정');
    const guardianName = contact.primary_guardian_contact
      ? `${contact.primary_guardian_contact.name}${contact.primary_guardian_contact.relationship
        ? `(${relationships[contact.primary_guardian_contact.relationship]})` : ''}`
      : '-';

    return {
      id: contact.id,
      cells: [
        {
          id: 'checkbox',
          content: (
            <S.Checkbox
              color="primary"
              checked={getOr(false, contact.id, checked)}
              onChange={() => setChecked(prevChecked => ({
                ...prevChecked,
                [contact.id]: !prevChecked[contact.id],
              }))}
            />
          ),
          props: { onClick: event => event.stopPropagation() },
        },
        { id: 'group', content: groupName },
        { id: 'clazzNumber', content: contact.number || '-' },
        { id: 'studentName', content: <S.Pad>{contact.name}</S.Pad> },
        { id: 'studentPhoneNumber', content: formatPhoneNumber(contact.mobile) },
        { id: 'guardianName', content: <S.Pad>{guardianName}</S.Pad> },
        { id: 'guardianPhoneNumber', content: formatPhoneNumber(get('mobile', contact.primary_guardian_contact)) },
      ],
      props: {
        'data-cid': contact.id,
        onClick: event => onOpen(toNumber(event.currentTarget.dataset.cid)),
      },
    };
  }, [checked, onOpen, relationships]);

  const counts = useMemo(() => ({
    checked: compose(
      size,
      pickBy(identity),
    )(checked),
    total: size(get('rows', contacts.data)),
  }), [checked, contacts.data]);

  const head = useMemo(() => ({
    id: 'head',
    cells: [
      {
        id: 'checkbox',
        content: (
          <S.Checkbox
            color="primary"
            checked={!isEmpty(checked) && checkedAll(checked)}
            onChange={toggleAllChecked}
            disabled={isEmpty(get('rows', contacts.data))}
            dark
          />
        ),
      },
      { id: 'group', content: <FormattedMessage {...messages.contactTableHeadGroup} /> },
      { id: 'clazzNumber', content: <FormattedMessage {...messages.contactTableHeadClazzNumber} /> },
      { id: 'studentName', content: <S.Pad><FormattedMessage {...messages.contactTableHeadStudentName} /></S.Pad> },
      { id: 'stduentPhoneNumber', content: <FormattedMessage {...messages.contactTableHeadStudentPhoneNumber} /> },
      { id: 'guardianName', content: <S.Pad><FormattedMessage {...messages.contactTableHeadGuardianName} /></S.Pad> },
      { id: 'guardianPhoneNumber', content: <FormattedMessage {...messages.contactTableHeadGuardianPhoneNumber} /> },
    ],
  }), [checked, checkedAll, contacts.data, toggleAllChecked]);

  const body = useMemo(() => ({
    rows: map(getRow, get('rows', contacts.data)),
  }), [getRow, contacts.data]);

  const handleClick = useCallback(() => {
    onDelete({ cids: keys(pickBy(identity, checked)) });
  }, [onDelete, checked]);

  useEffect(() => {
    if (contactsDeleted.data) {
      setChecked({});
    }
  }, [contactsDeleted.data]);

  if (get('rows', contacts.data) !== prevContacts) {
    setChecked(prevChecked => ({
      ...reduce((acc, cur) => ({
        ...acc,
        [cur.id]: false,
      }), {}, getOr({}, 'rows', contacts.data)),
      ...!isEmpty(get('rows', contacts.data)) ? prevChecked : {},
    }));

    setPrevContacts(get('rows', contacts.data));
  }

  return (
    <S.Wrapper>
      {contacts.loading
        ? <Shimmer />
        : (
          <>
            <Table
              cols={cols}
              head={head}
              body={body}
              tableRow={S.TableRow}
              tableCell={S.TableCell}
            />
            {checkedSome && (
              <S.TableToolBar
                all={all}
                checkAll={checkAll}
                uncheckAll={uncheckAll}
                checked={checked}
                checkedAll={checkedAll}
                toggleAllChecked={toggleAllChecked}
                onOpen={openDeleteConfirmDialog}
                onSelect={onSelect}
                onDeselect={onDeselect}
              />
            )}
          </>
        )}
      <DeleteConfirmDialog
        counts={counts}
        open={open === 'deleteConfirm'}
        onClose={closeDialog}
        onDelete={openDeleteReconfirmDialog}
      />
      <DeleteReconfirmDialog
        counts={counts}
        open={open === 'deleteReconfirm'}
        onClose={closeDialog}
        onDelete={handleClick}
      />
    </S.Wrapper>
  );
};

ContactTable.propTypes = {
  all: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
  contacts: PropTypes.shape({
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          clazzNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ),
      total: PropTypes.string,
      perPage: PropTypes.string,
    }),
    loading: PropTypes.bool,
    error: PropTypes.instanceOf(Error),
  }),
  contactsDeleted: PropTypes.shape({
    data: PropTypes.bool,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDeselect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

ContactTable.defaultProps = {
  contacts: { data: null, loading: false, error: null },
};

export default withRouter(ContactTable);
